import React, { useState } from 'react';
import {
  Mail,
  Phone,
  MapPin,
  Linkedin,
  Twitter,
  Facebook,
  Send,
  Loader2,
  CheckCircle2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { companyInfo } from '../data/mock';

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({
    title: '',
    description: '',
    type: 'success', // 'success' or 'error'
  });
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    industry: '',
    inquiryType: '',
    message: '',
    terms: false,
  });

  const [errors, setErrors] = useState({});

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[0-9]{10}$/;
    return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''));
  };

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone validation
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 3) {
      newErrors.message = 'Message must be at least 3 characters';
    }

    // Terms validation
    if (!formData.terms) {
      newErrors.terms = 'You must accept the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous errors
    setErrors({});

    // Validate form
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare data for API
      const apiData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        company: formData.company.trim() || '',
        industry: formData.industry || '',
        inquiry_type: formData.inquiryType || '',
        message: formData.message.trim(),
      };

      // Make API call
      const response = await fetch(
        'https://www.greencokeenergy.com/api/sendemail.php',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(apiData),
        },
      );

      // Check if response is ok
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Parse response
      const result = await response.json();

      // Check for success in response
      if (result.success || response.ok) {
        setModalContent({
          title: 'Inquiry Sent Successfully!',
          description:
            "Thank you for your interest. We'll get back to you within 24 hours.",
          type: 'success',
        });
        setModalOpen(true);

        // Reset form
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          industry: '',
          inquiryType: '',
          message: '',
          terms: false,
        });
      } else {
        throw new Error(result.message || 'Failed to send inquiry');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setModalContent({
        title: 'Submission Failed',
        description:
          error.message ||
          'There was an error sending your inquiry. Please try again or contact us directly.',
        type: 'error',
      });
      setModalOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleCheckboxChange = (checked) => {
    setFormData((prev) => ({ ...prev, terms: checked }));

    // Clear error for terms
    if (errors.terms) {
      setErrors((prev) => ({ ...prev, terms: '' }));
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-[#1B4D40] py-16 md:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Get in Touch
          </h1>
          <p className="text-lg md:text-xl text-gray-200 max-w-2xl mx-auto">
            Connect with Green Coke and Energy for inquiries, partnerships, and
            business opportunities
          </p>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Left Column - Contact Info */}
            <div className="lg:col-span-2 space-y-8">
              {/* Office Address */}
              <div>
                <h3 className="text-xl font-bold text-[#1B4D40] mb-4">
                  Office Address
                </h3>
                <div className="space-y-2 text-[#2C3E50]">
                  {/* <p>{companyInfo.contact.address.line1}</p>
                  <p>{companyInfo.contact.address.line2}</p>
                  <p>
                    {companyInfo.contact.address.city},{' '}
                    {companyInfo.contact.address.state}{' '}
                    {companyInfo.contact.address.pincode}
                  </p>
                  <p>{companyInfo.contact.address.country}</p> */}
                  <p>
                    Ground Floor, New No.11, 2nd Link Street, CIT Colony,
                    Mylapore, Chennai, Tamil Nadu, India, 600004
                  </p>
                  <p>
                    <b>CIN</b> : U19109TN2025PTC183276{' '}
                  </p>
                  <p>
                    <b>Company Name</b> : GREEN COKE AND ENERGY PRIVATE LIMITED
                  </p>
                  {/* <p>
                    <b>ROC Name</b> : ROC Chennai{' '}
                  </p> */}
                  {/* <p>
                    <b>Registration Number</b> : 183276
                  </p> */}
                  {/* <p>
                    <b>Date of Incorporation</b> : 14/08/2025
                  </p> */}
                  {/* <p>
                    <b>Email Id </b>:{' '}
                    <span>
                      <a href="mailto:info@greencokeenergy.com">
                        info@greencokeenergy.com
                      </a>
                    </span>
                  </p> */}
                </div>
              </div>

              {/* Contact Numbers */}
              <div>
                <h3 className="text-xl font-bold text-[#1B4D40] mb-4">
                  Contact Numbers
                </h3>
                <ul className="space-y-3">
                  <a href="tel:+919176651276">
                    <li className="flex items-start space-x-3 hover:text-[#C0D8A3] transition-colors">
                      <Phone className="h-5 w-5 text-[#C0D8A3] flex-shrink-0 mt-0.5" />
                      {/* <p className="text-sm font-medium text-[#1B4D40]">Main</p> */}
                      <p className="text-[#2C3E50]">+919176651276</p>
                    </li>
                  </a>
                  {/* <li className="flex items-start space-x-3">
                    <Phone className="h-5 w-5 text-[#C0D8A3] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-[#1B4D40]">
                        Trading Inquiries
                      </p>
                      <p className="text-[#2C3E50]">
                        {companyInfo.contact.phones.trading}
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start space-x-3">
                    <Phone className="h-5 w-5 text-[#C0D8A3] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-[#1B4D40]">
                        General
                      </p>
                      <p className="text-[#2C3E50]">
                        {companyInfo.contact.phones.general}
                      </p>
                    </div>
                  </li> */}
                </ul>
              </div>

              {/* Email Addresses */}
              <div>
                <h3 className="text-xl font-bold text-[#1B4D40] mb-4">
                  Email Addresses
                </h3>
                <ul className="space-y-3">
                  <a href="mailto:info@greencokeenergy.com">
                    <li className="flex items-start space-x-3 hover:text-[#C0D8A3] transition-colors">
                      <Mail className="h-5 w-5 text-[#C0D8A3] flex-shrink-0 mt-0.5" />

                      {/* <p className="text-sm font-medium text-[#1B4D40]">
                        General Inquiries
                      </p> */}
                      <p className="text-[#2C3E50]">info@greencokeenergy.com</p>
                    </li>
                  </a>
                  {/* <li className="flex items-start space-x-3">
                    <Mail className="h-5 w-5 text-[#C0D8A3] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-[#1B4D40]">
                        Trading
                      </p>
                      <p className="text-[#2C3E50]">
                        {companyInfo.contact.emails.trading}
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start space-x-3">
                    <Mail className="h-5 w-5 text-[#C0D8A3] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-[#1B4D40]">
                        Careers
                      </p>
                      <p className="text-[#2C3E50]">
                        {companyInfo.contact.emails.hr}
                      </p>
                    </div>
                  </li> */}
                </ul>
              </div>

              {/* Business Hours */}
              {/* <div>
                <h3 className="text-xl font-bold text-[#1B4D40] mb-4">
                  Business Hours
                </h3>
                <ul className="space-y-2 text-[#2C3E50]">
                  <li>{companyInfo.contact.hours.weekdays}</li>
                  <li>{companyInfo.contact.hours.saturday}</li>
                  <li>{companyInfo.contact.hours.sunday}</li>
                </ul>
              </div> */}

              {/* Map */}
              {/* <div className="bg-gray-200 rounded-lg h-64 flex items-center justify-center">
                <MapPin className="h-12 w-12 text-gray-400" />
                <p className="ml-3 text-gray-500">Map will be embedded here</p>
              </div> */}
            </div>

            {/* Right Column - Contact Form */}
            <div className="lg:col-span-3">
              <Card>
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold text-[#1B4D40] mb-6">
                    Send us a Message
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name">
                          Name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className={`mt-1.5 ${
                            errors.name ? 'border-red-500' : ''
                          }`}
                          disabled={isSubmitting}
                        />
                        {errors.name && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors.name}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="company">Company Name</Label>
                        <Input
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          className="mt-1.5"
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="email">
                          Email <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className={`mt-1.5 ${
                            errors.email ? 'border-red-500' : ''
                          }`}
                          disabled={isSubmitting}
                        />
                        {errors.email && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors.email}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="phone">
                          Phone <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className={`mt-1.5 ${
                            errors.phone ? 'border-red-500' : ''
                          }`}
                          disabled={isSubmitting}
                        />
                        {errors.phone && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors.phone}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="industry">Industry</Label>
                        <Select
                          value={formData.industry}
                          onValueChange={(value) =>
                            handleSelectChange('industry', value)
                          }
                          disabled={isSubmitting}
                        >
                          <SelectTrigger className="mt-1.5">
                            <SelectValue placeholder="Select your industry" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="steel">
                              Steel Manufacturing
                            </SelectItem>
                            <SelectItem value="ferroalloy">
                              Ferro-Alloy Production
                            </SelectItem>
                            <SelectItem value="foundry">
                              Foundry Operations
                            </SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="inquiryType">Inquiry Type</Label>
                        <Select
                          value={formData.inquiryType}
                          onValueChange={(value) =>
                            handleSelectChange('inquiryType', value)
                          }
                          disabled={isSubmitting}
                        >
                          <SelectTrigger className="mt-1.5">
                            <SelectValue placeholder="Select inquiry type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="products">
                              Product Information
                            </SelectItem>
                            <SelectItem value="trading">
                              Trading Services
                            </SelectItem>
                            <SelectItem value="partnership">
                              Partnership Opportunities
                            </SelectItem>
                            <SelectItem value="general">
                              General Inquiry
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message">
                        Message <span className="text-red-500">*</span>
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        rows={6}
                        className={`mt-1.5 ${
                          errors.message ? 'border-red-500' : ''
                        }`}
                        disabled={isSubmitting}
                      />
                      {errors.message && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <div className="flex items-start space-x-2">
                        <Checkbox
                          id="terms"
                          checked={formData.terms}
                          onCheckedChange={handleCheckboxChange}
                          className={`mt-1 ${
                            errors.terms ? 'border-red-500' : ''
                          }`}
                          disabled={isSubmitting}
                        />
                        <Label
                          htmlFor="terms"
                          className="text-sm text-[#2C3E50] cursor-pointer"
                        >
                          I agree to the terms and conditions and privacy policy{' '}
                          <span className="text-red-500">*</span>
                        </Label>
                      </div>
                      {errors.terms && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.terms}
                        </p>
                      )}
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full bg-[#C0D8A3] hover:bg-[#27ae60] text-white font-medium transition-all"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          Send Inquiry
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Social Media Section */}
      {/* <section className="bg-[#F5F1E8] py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-[#1B4D40] mb-4">
            Follow Us for Updates & News
          </h2>
          <div className="flex justify-center space-x-4 mt-6">
            <a
              href={companyInfo.social.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="p-4 bg-white rounded-lg hover:bg-[#C0D8A3] hover:text-white transition-colors group"
              aria-label="LinkedIn"
            >
              <Linkedin className="h-6 w-6 text-[#1B4D40] group-hover:text-white" />
            </a>
            <a
              href={companyInfo.social.twitter}
              target="_blank"
              rel="noopener noreferrer"
              className="p-4 bg-white rounded-lg hover:bg-[#C0D8A3] hover:text-white transition-colors group"
              aria-label="Twitter"
            >
              <Twitter className="h-6 w-6 text-[#1B4D40] group-hover:text-white" />
            </a>
            <a
              href={companyInfo.social.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="p-4 bg-white rounded-lg hover:bg-[#C0D8A3] hover:text-white transition-colors group"
              aria-label="Facebook"
            >
              <Facebook className="h-6 w-6 text-[#1B4D40] group-hover:text-white" />
            </a>
          </div>
        </div>
      </section> */}

      {/* Newsletter Section */}
      {/* <section className="py-16 md:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1B4D40] mb-4">
              Stay Updated
            </h2>
            <p className="text-lg text-[#2C3E50] mb-6">
              Subscribe to our newsletter for updates on our commissioning
              progress, industry insights, and product availability.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                className="flex-1"
              />
              <Button className="bg-[#C0D8A3] hover:bg-[#27ae60] text-white">
                Subscribe
              </Button>
            </div>
            <p className="text-sm text-gray-500 mt-4">
              Frequency: Monthly updates
            </p>
          </div>
        </div>
      </section> */}

      {/* Success/Error Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {modalContent.type === 'success' ? (
                <>
                  <CheckCircle2 className="h-6 w-6 text-green-600" />
                  <span className="text-green-600">{modalContent.title}</span>
                </>
              ) : (
                <>
                  <Mail className="h-6 w-6 text-red-600" />
                  <span className="text-red-600">{modalContent.title}</span>
                </>
              )}
            </DialogTitle>
            <DialogDescription className="text-base pt-2">
              {modalContent.description}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end mt-4">
            <Button
              onClick={() => setModalOpen(false)}
              className={
                modalContent.type === 'success'
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-gray-600 hover:bg-gray-700 text-white'
              }
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Contact;
